import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { partnersAPI, customFieldsAPI } from '../services/api';
import { scrollAndFlash } from '../lib/highlight';
import { REGIONS, COUNTRIES } from '../constants/options';
import { useAuth } from '../context/AuthContext';
import { EntityCustomFields, renderCustomValue } from '../components/shared/EntityCustomFields';
import { ActiveFiltersBar } from '../components/shared/ActiveFiltersBar';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { TablePagination, PAGE_SIZE } from '../components/shared/TablePagination';
import { ColumnFilterPopover } from '../components/shared/ColumnFilterPopover';
import { 
  Plus, 
  Search, 
  Edit2, 
  Trash2,
  Building2,
  Globe,
  Mail,
  User,
  MapPin
} from 'lucide-react';
import { toast } from 'sonner';

export const PartnersPage = () => {
  const { user } = useAuth();
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [partnerFields, setPartnerFields] = useState([]);
  const [partnersPage, setPartnersPage] = useState(1);
  const [colFilters, setColFilters] = useState({ region: new Set(), country: new Set(), status: new Set() });
  const [customColFilters, setCustomColFilters] = useState({});
  const setColFilter = (key, s) => setColFilters(prev => ({ ...prev, [key]: s }));
  const setCustomColFilter = (tid, s) => setCustomColFilters(prev => ({ ...prev, [tid]: s }));
  const [search, setSearch] = useState('');
  const [searchParams] = useSearchParams();
  useEffect(() => { const q = searchParams.get('q'); if (q) setSearch(q); }, [searchParams]);
  useEffect(() => {
    const hid = searchParams.get('highlight');
    if (!hid || !partners.length) return;
    const t = setTimeout(() => scrollAndFlash(`partner-row-${hid}`), 350);
    return () => clearTimeout(t);
  }, [searchParams, partners]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingPartner, setEditingPartner] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    contact_name: '',
    contact_email: '',
    region: '',
    country: '',
    custom_fields: {},
  });

  useEffect(() => {
    loadPartners();
    customFieldsAPI.list('partner').then(setPartnerFields).catch(() => {});
  }, []);

  const loadPartners = async () => {
    try {
      setLoading(true);
      const data = await partnersAPI.getAll();
      setPartners(data);
    } catch (error) {
      console.error('Failed to load partners:', error);
      toast.error('Failed to load partners');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (partner = null) => {
    if (partner) {
      setEditingPartner(partner);
      setFormData({
        name: partner.name,
        code: partner.code,
        contact_name: partner.contact_name || '',
        contact_email: partner.contact_email || '',
        region: partner.region || '',
        country: partner.country || '',
        custom_fields: partner.custom_fields || {},
      });
    } else {
      setEditingPartner(null);
      setFormData({
        name: '',
        code: '',
        contact_name: '',
        contact_email: '',
        region: '',
        country: '',
        custom_fields: {},
      });
    }
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.code) {
      toast.error('Name and code are required');
      return;
    }

    try {
      if (editingPartner) {
        await partnersAPI.update(editingPartner.id, formData);
        toast.success('Partner updated successfully');
      } else {
        await partnersAPI.create(formData);
        toast.success('Partner created successfully');
      }
      setIsDialogOpen(false);
      loadPartners();
    } catch (error) {
      console.error('Failed to save partner:', error);
      toast.error(error.response?.data?.detail || 'Failed to save partner');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to deactivate this partner?')) {
      return;
    }
    
    try {
      await partnersAPI.delete(id);
      toast.success('Partner deactivated');
      loadPartners();
    } catch (error) {
      console.error('Failed to delete partner:', error);
      toast.error('Failed to deactivate partner');
    }
  };

  const filteredPartners = partners.filter(partner => {
    if (search) {
      const searchLower = search.toLowerCase();
      if (!(partner.name.toLowerCase().includes(searchLower) ||
        partner.code.toLowerCase().includes(searchLower) ||
        partner.region?.toLowerCase().includes(searchLower) ||
        partner.country?.toLowerCase().includes(searchLower))) return false;
    }
    if (colFilters.region.size && !colFilters.region.has(partner.region)) return false;
    if (colFilters.country.size && !colFilters.country.has(partner.country)) return false;
    if (colFilters.status.size && !colFilters.status.has(partner.is_active ? 'active' : 'inactive')) return false;
    for (const [tid, set] of Object.entries(customColFilters)) {
      if (!set || !set.size) continue;
      const raw = partner.custom_fields?.[tid];
      const vals = Array.isArray(raw) ? raw : (raw ? [raw] : []);
      if (!vals.some(v => set.has(v))) return false;
    }
    return true;
  });

  const regionOpts = [...new Set(partners.map(p => p.region).filter(Boolean))].sort().map(v => ({ value: v, label: (REGIONS.find(r => r.value === v)?.label) || v }));
  const countryOpts = [...new Set(partners.map(p => p.country).filter(Boolean))].sort().map(v => ({ value: v, label: v }));
  const statusOpts = [{ value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' }];
  const customColOptsFor = (f) => (f.options || []).map(o => ({ value: o.value ?? o, label: o.label ?? o }));
  const activeFilters = [
    { key: 'region', label: 'Region', set: colFilters.region, options: regionOpts, onChange: (s) => setColFilter('region', s) },
    { key: 'country', label: 'Country', set: colFilters.country, options: countryOpts, onChange: (s) => setColFilter('country', s) },
    { key: 'status', label: 'Status', set: colFilters.status, options: statusOpts, onChange: (s) => setColFilter('status', s) },
    ...partnerFields.map(f => ({ key: f.taxonomy_id, label: f.label, set: customColFilters[f.taxonomy_id] || new Set(), options: customColOptsFor(f), onChange: (s) => setCustomColFilter(f.taxonomy_id, s) })),
  ];

  useEffect(() => { setPartnersPage(1); }, [search, colFilters, customColFilters]);
  const pagedPartners = filteredPartners.slice((partnersPage - 1) * PAGE_SIZE, partnersPage * PAGE_SIZE);

  const getRegionLabel = (value) => {
    const region = REGIONS.find(r => r.value === value);
    return region?.label || value || '-';
  };

  const getCountryLabel = (value) => {
    const country = COUNTRIES.find(c => c.value === value);
    return country?.label || value || '-';
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="partners-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold">Partners</h1>
          <p className="text-muted-foreground">
            Manage partner organizations
          </p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button 
              onClick={() => handleOpenDialog()}
              data-testid="add-partner-button"
              className="bg-lime text-lime-foreground hover:bg-lime/90"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Partner
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>
                {editingPartner ? 'Edit Partner' : 'Add New Partner'}
              </DialogTitle>
              <DialogDescription>
                {editingPartner 
                  ? 'Update partner information' 
                  : 'Create a new partner organization'}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Ernst & Young"
                    data-testid="partner-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="code">Code *</Label>
                  <Input
                    id="code"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                    placeholder="EY"
                    maxLength={10}
                    data-testid="partner-code-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="contact_name">Contact Name</Label>
                  <Input
                    id="contact_name"
                    value={formData.contact_name}
                    onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                    placeholder="John Smith"
                    data-testid="contact-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contact_email">Contact Email</Label>
                  <Input
                    id="contact_email"
                    type="email"
                    value={formData.contact_email}
                    onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                    placeholder="contact@partner.com"
                    data-testid="contact-email-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="region">Region</Label>
                  <Select 
                    value={formData.region} 
                    onValueChange={(v) => setFormData({ ...formData, region: v })}
                  >
                    <SelectTrigger data-testid="region-select">
                      <SelectValue placeholder="Select region" />
                    </SelectTrigger>
                    <SelectContent>
                      {REGIONS.map(region => (
                        <SelectItem key={region.value} value={region.value}>
                          {region.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="country">Country</Label>
                  <Select 
                    value={formData.country} 
                    onValueChange={(v) => setFormData({ ...formData, country: v })}
                  >
                    <SelectTrigger data-testid="country-select">
                      <SelectValue placeholder="Select country" />
                    </SelectTrigger>
                    <SelectContent className="max-h-64">
                      {COUNTRIES.map(country => (
                        <SelectItem key={country.value} value={country.value}>
                          {country.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <EntityCustomFields
                entityType="partner"
                values={formData.custom_fields || {}}
                onChange={(v) => setFormData({ ...formData, custom_fields: v })}
                isSuperAdmin={user?.role === 'superadmin'}
                currentUser={user}
                onSchemaChanged={() => customFieldsAPI.list('partner').then(setPartnerFields).catch(() => {})}
                testPrefix="partner-custom-field"
              />
              <div className="flex justify-end gap-3 pt-4">
                <Button 
                  type="button" 
                  variant="outline"
                  onClick={() => setIsDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button 
                  type="submit"
                  className="bg-lime text-lime-foreground hover:bg-lime/90"
                  data-testid="save-partner-button"
                >
                  {editingPartner ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search partners..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
              data-testid="search-partners"
            />
          </div>
        </CardContent>
      </Card>

      <ActiveFiltersBar filters={activeFilters} search={search} onClearSearch={() => setSearch('')} testId="partners-active-filters" />

      <Card>
        <CardContent className="pt-6">
          {loading ? (
            <div className="space-y-4">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-16 bg-muted animate-pulse rounded" />
              ))}
            </div>
          ) : filteredPartners.length > 0 ? (
            <Table containerClassName="max-h-[calc(100vh-300px)]">
              <TableHeader sticky>
                <TableRow>
                  <TableHead>Partner</TableHead>
                  <TableHead>Code</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead><ColumnFilterPopover label="Region" options={regionOpts} selected={colFilters.region} onChange={(s) => setColFilter('region', s)} testId="partners-col-filter-region" /></TableHead>
                  <TableHead><ColumnFilterPopover label="Country" options={countryOpts} selected={colFilters.country} onChange={(s) => setColFilter('country', s)} testId="partners-col-filter-country" /></TableHead>
                  <TableHead><ColumnFilterPopover label="Status" options={statusOpts} selected={colFilters.status} onChange={(s) => setColFilter('status', s)} testId="partners-col-filter-status" /></TableHead>
                  {partnerFields.map(f => (
                    <TableHead key={f.id}><ColumnFilterPopover label={f.label} options={customColOptsFor(f)} selected={customColFilters[f.taxonomy_id] || new Set()} onChange={(s) => setCustomColFilter(f.taxonomy_id, s)} testId={`partners-col-filter-${f.taxonomy_id}`} /></TableHead>
                  ))}
                  <TableHead className="w-24">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pagedPartners.map((partner) => (
                  <TableRow key={partner.id} data-testid={`partner-row-${partner.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-secondary/10 flex items-center justify-center">
                          <Building2 className="w-5 h-5 text-secondary" />
                        </div>
                        <span className="font-medium">{partner.name}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary">{partner.code}</Badge>
                    </TableCell>
                    <TableCell>
                      {partner.contact_name ? (
                        <div>
                          <div className="flex items-center gap-1 text-sm">
                            <User className="w-3 h-3" />
                            {partner.contact_name}
                          </div>
                          {partner.contact_email && (
                            <div className="flex items-center gap-1 text-xs text-muted-foreground">
                              <Mail className="w-3 h-3" />
                              {partner.contact_email}
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Globe className="w-4 h-4 text-muted-foreground" />
                        {getRegionLabel(partner.region)}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <MapPin className="w-4 h-4 text-muted-foreground" />
                        {getCountryLabel(partner.country)}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={partner.is_active ? 'default' : 'secondary'}>
                        {partner.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    {partnerFields.map(f => (
                      <TableCell key={f.id} className="text-sm">{renderCustomValue(partner.custom_fields?.[f.taxonomy_id], f.options) || <span className="text-xs text-muted-foreground">—</span>}</TableCell>
                    ))}
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleOpenDialog(partner)}
                          data-testid={`edit-partner-${partner.id}`}
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(partner.id)}
                          className="text-destructive hover:text-destructive"
                          data-testid={`delete-partner-${partner.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-12">
              <Building2 className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-2">No partners found</h3>
              <p className="text-muted-foreground mb-4">
                {search ? 'Try adjusting your search' : 'Get started by adding your first partner'}
              </p>
              {!search && (
                <Button onClick={() => handleOpenDialog()}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Partner
                </Button>
              )}
            </div>
          )}
          <TablePagination page={partnersPage} total={filteredPartners.length} onPageChange={setPartnersPage} testId="partners-pagination" itemLabel="partners" />
        </CardContent>
      </Card>
    </div>
  );
};

export default PartnersPage;
